// src/pages/info/DBSeries.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Select from 'react-select';
import './DBSeries.css';
import DeleteSeriesModal from '../../../components/Modals/DeleteSeriesModal';
import Modal from 'react-modal';
import API_BASE_URL from '../../../config';

Modal.setAppElement('#root');

// Утилита для разбора блоков описания по маркеру @@
const parseBlocks = (raw) =>
  raw
    ?.split('@@')
    .map((s) => s.trim())
    .filter(Boolean) || [];

const DBSeries = () => {
  const location = useLocation();
  const fromHouse = location.state?.from === 'house';
  const fromEditHouse = location.state?.from === 'editHouse';
  const house = location.state?.house;

  // Если пришли из дома — сразу ставим ту серию (через её ID)
  const rawId = location.state?.selectedId;
  const initialId = rawId != null ? Number(rawId) : null;

  const [selectedSeries, setSelectedSeries] = useState(location.state?.series || null);
  const [seriesList, setSeriesList] = useState([]);
  const [seriesOptions, setSeriesOptions] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState('');

  const navigate = useNavigate();

  // Загрузка списка серий из бэкенда
  useEffect(() => {
    fetch(`${API_BASE_URL}/building/series_all`)
      .then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
      .then((data) => {
        setSeriesList(data);
        const opts = data.map((item) => ({ value: item.id, label: item.name }));
        setSeriesOptions(opts);
        if (initialId !== null && !Number.isNaN(initialId)) {
          const pre = opts.find((o) => o.value === initialId);
          setSelectedSeries(pre);
        }
      })
      .catch((err) => console.error('Ошибка загрузки серий:', err));
  }, [initialId]);

  // Данные текущей выбранной серии
  const current = useMemo(
    () => seriesList.find((s) => s.id === selectedSeries?.value),
    [seriesList, selectedSeries]
  );

  // Генерация HTML описания
  const fullHtml = useMemo(() => {
    if (!current?.description) return '';
    const blocks = parseBlocks(current.description);
    return blocks
      .map((txt) => {
        const m = /^\*\*(.+)\*\*$/.exec(txt);
        if (m) return `<p><strong>${m[1].trim()}</strong></p>`;
        return `<p>${txt}</p>`;
      })
      .join('');
  }, [current]);

  // Открытие lightbox
  const openLightbox = (url) => {
    setLightboxSrc(url);
    setLightboxOpen(true);
  };

  // Обработчик клика по кнопке "Удалить серию" — открываем модалку
  const handleDelete = () => {
    if (!current) return;
    setShowDeleteModal(true);
  };

  // Подтверждение удаления: посылаем DELETE-запрос и обновляем локальный список
  const confirmDelete = () => {
    if (!current) {
      setShowDeleteModal(false);
      return;
    }

    fetch(`${API_BASE_URL}/building/series_delete/${current.id}`, {
      method: 'DELETE',
    })
      .then((res) => {
        if (!res.ok) {
          return res.text().then((text) => Promise.reject(text || res.status));
        }
        // Если сервер возвращает JSON или пустой ответ
        return res.json().catch(() => null);
      })
      .then(() => {
        // Успешно удалили — обновляем локальное состояние
        setSeriesList((prev) => prev.filter((s) => s.id !== current.id));
        setSeriesOptions((prev) => prev.filter((opt) => opt.value !== current.id));
        setSelectedSeries(null);
        setShowDeleteModal(false);
      })
      .catch((err) => {
        console.error('Ошибка при удалении серии:', err);
        setShowDeleteModal(false);
      });
  };

  // Обработчик клика по кнопке "Редактировать серию"
  const handleEdit = () => {
    if (!current) return;
    navigate(`/edit-db-series/${current.id}`, {
      state: {
        selectedId: current.id,
        from: fromEditHouse ? 'editHouse' : fromHouse ? 'house' : undefined,
        house: house,
      },
    });
  };

  return (
    <div className="check-page series_page">
      {/* Header */}
      <div className="edit-header-home header_series">
        {(fromHouse || fromEditHouse) && (
          <button
            className="back-btn-home"
            onClick={() => {
              if (fromEditHouse && house) {
                navigate(`/db-house/${house.id}`, { state: house });
              } else {
                navigate(-1);
              }
            }}
          >
            <span className="back-icon-home">⌄</span>
          </button>
        )}
        <h1 className="page-title series_title">
          База данных серий домов в г. Норильск
        </h1>

        <div className="drawing-select-block">
          <Select
            options={seriesOptions}
            value={selectedSeries}
            onChange={(opt) => setSelectedSeries(opt)}
            placeholder="Выбор серии дома"
            isSearchable
            menuPortalTarget={document.body}
            menuPosition="absolute"
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              control: (base, state) => ({
                ...base,
                backgroundColor: 'transparent',
                borderColor: state.isFocused ? '#FF740F' : '#3B3B40',
                '&:hover': { borderColor: '#FF740F' },
                borderRadius: 6,
                minHeight: 36,
                height: 36,
                boxShadow: 'none',
                padding: '0 8px',
              }),
              valueContainer: (base) => ({ ...base, height: 36, padding: '0 8px' }),
              indicatorsContainer: (base) => ({ ...base, height: 36 }),
              indicatorSeparator: () => ({ display: 'none' }),
              singleValue: (base) => ({ ...base, color: '#e5e7eb' }),
              input: (base) => ({ ...base, color: '#e5e7eb' }), // Вводимый текст — белый
              placeholder: (base) => ({ ...base, color: '#8b919d' }),
              menu: (base) => ({ ...base, backgroundColor: '#2a2c33' }),
              option: (base, state) => ({
                ...base,
                backgroundColor: state.isFocused ? '#3B3B40' : '#2a2c33',
                color: '#e5e7eb',
              }),
            }}
          />
        </div>

        <div className="edit-actions">
          <button className="primary-btn" onClick={handleDelete}>
            Удалить серию
          </button>
          <button className="primary-btn nn" onClick={handleEdit}>
            Редактировать серию
          </button>
        </div>
      </div>

      {/* Body */}
      {current ? (
        <div className="edit-house-body">
          <div className="edit-divider" />

          {/* Левый блок: таблица с данными серии */}
          <div className="edit-house-left">
            <div className="edit-house-table-container">
              <div className="edit-house-table-wrapper">
                <table className="edit-house-table">
                  <tbody>
                    <tr className="edit-house-table__row-series">
                      <td className="edit-house-table__cell edit-house-table__cell--label">
                        Серия дома
                      </td>
                      <td className="edit-house-table__cell edit-house-table__cell--value ppp">
                        {current.name}
                      </td>
                    </tr>
                    <tr className="edit-house-table__row-series">
                      <td className="edit-house-table__cell edit-house-table__cell--label">
                        Описание
                      </td>
                      <td className="edit-house-table__cell edit-house-table__cell--value">
                        <div className="scroll-wrapper allal">
                          <div
                            className="HD-text"
                            dangerouslySetInnerHTML={{ __html: fullHtml }}
                          />
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="edit-divider" />

          {/* Правый блок: фотогалерея */}
          <div className="edit-house-right">
            <div className="collector-photo-gallery">
              {current.images?.length ? (
                current.images.map((img, i) => (
                  <div
                    key={i}
                    className="collector-photo-item"
                    onClick={() => openLightbox(img.url)}
                    style={{ cursor: 'zoom-in' }}
                  >
                    <img src={img.url} alt={`Чертёж ${i + 1}`} />
                  </div>
                ))
              ) : (
                <div className="no-drawings-message">
                  Чертежей у этой серии нет
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="edit-house-body">
          <div className="edit-house-left padd-left durak">
            <div className="text-block">
              <p className="text-content">
                Для отображения информации
                <br />
                выберите вариант из выпадающего
                <br />
                списка
              </p>
            </div>
          </div>
          <div className="edit-divider" />
          <div className="edit-house-right durak">
            <div className="text-block">
              <p className="text-content">
                Для отображения чертежей
                <br />
                выберите вариант из выпадающего
                <br />
                списка
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox для полноэкранного просмотра */}
      <Modal
        isOpen={lightboxOpen}
        onRequestClose={() => setLightboxOpen(false)}
        className="lightbox-modal"
        overlayClassName="lightbox-overlay"
      >
        <img
          src={lightboxSrc}
          alt="Чертёж во весь экран"
          className="lightbox-image"
          onClick={() => setLightboxOpen(false)}
        />
      </Modal>

      {/* Модалка подтверждения удаления */}
      {showDeleteModal && (
        <DeleteSeriesModal
          onClose={() => setShowDeleteModal(false)}
          onConfirm={confirmDelete}
        />
      )}
    </div>
  );
};

export default DBSeries;
